import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Button, Input, Card, CardContent } from '@/components/ui';
import { trpc } from '@/lib/trpc';

const resetFormSchema = z
  .object({
    novaSenha: z.string().min(6, 'Nova senha deve ter no mínimo 6 caracteres'),
    confirmarSenha: z.string().min(6, 'Confirme a nova senha'),
  })
  .refine((data) => data.novaSenha === data.confirmarSenha, {
    message: 'As senhas não coincidem',
    path: ['confirmarSenha'],
  });

type ResetFormData = z.infer<typeof resetFormSchema>;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const form = useForm<ResetFormData>({
    resolver: zodResolver(resetFormSchema),
  });

  const mutation = trpc.auth.resetPassword.useMutation({
    onError: (err: { message: string }) => {
      form.setError('root', { message: err.message });
    },
  });

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-600 to-primary-800">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <h1 className="text-2xl font-bold text-primary-600 mb-4">Portal LEO Clube</h1>
            <p className="text-red-600 font-medium mb-4">Link inválido</p>
            <p className="text-sm text-gray-600 mb-4">
              O link de redefinição de senha é inválido ou está incompleto.
            </p>
            <Link
              to="/login"
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Voltar para o login
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const onSubmit = (data: ResetFormData) => {
    mutation.mutate({ token, novaSenha: data.novaSenha });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-600 to-primary-800">
      <Card className="w-full max-w-md">
        <CardContent className="p-8">
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-primary-600">Portal LEO Clube</h1>
            <p className="mt-2 text-gray-600">Redefinir senha</p>
          </div>

          {mutation.isSuccess ? (
            <div className="space-y-4 text-center">
              <p className="text-green-600 font-medium">Senha redefinida com sucesso!</p>
              <p className="text-sm text-gray-600">
                Agora você pode fazer login com sua nova senha.
              </p>
              <Link
                to="/login"
                className="inline-block text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                Ir para o login
              </Link>
            </div>
          ) : (
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <Input
                id="novaSenha"
                type="password"
                label="Nova senha"
                placeholder="Mínimo 6 caracteres"
                error={form.formState.errors.novaSenha?.message}
                {...form.register('novaSenha')}
              />

              <Input
                id="confirmarSenha"
                type="password"
                label="Confirmar nova senha"
                placeholder="Repita a nova senha"
                error={form.formState.errors.confirmarSenha?.message}
                {...form.register('confirmarSenha')}
              />

              {form.formState.errors.root && (
                <p className="text-sm text-red-600">{form.formState.errors.root.message}</p>
              )}

              <Button type="submit" className="w-full" isLoading={mutation.isPending}>
                Redefinir senha
              </Button>

              <p className="text-center text-sm text-gray-600">
                <Link
                  to="/login"
                  className="font-medium text-primary-600 hover:text-primary-700"
                >
                  Voltar para o login
                </Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
